import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Receipt, Pencil } from "lucide-react";
import { toast } from "sonner";
import { AuthImage } from "@/components/AuthImage";
import { FileUploadButton } from "@/components/FileUploadButton";
import { formatError } from "@/lib/api";
import { useCollection } from "./useCollection";

const empty = { category: "food", amount: "", currency: "USD", merchant: "", date: "", photo_file_id: null, notes: "" };
const CATEGORIES = ["food", "transport", "lodging", "activity", "shopping", "other"];
const CURRENCIES = ["USD", "EUR", "GBP", "JPY", "INR", "CAD", "AUD", "CHF", "CNY", "MXN"];

export default function ReceiptsSection({ tripId, onChange }) {
  const { items, loading, create, update, remove } = useCollection(tripId, "receipts", onChange);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  function openNew() { setEditingId(null); setForm(empty); setOpen(true); }
  function openEdit(item) {
    setEditingId(item.id);
    setForm({ ...empty, ...Object.fromEntries(Object.entries(item).filter(([k]) => k in empty)), amount: String(item.amount ?? "") });
    setOpen(true);
  }
  function handleOpenChange(v) { setOpen(v); if (!v) { setEditingId(null); setForm(empty); } }

  async function save() {
    if (form.amount === "" || isNaN(Number(form.amount))) { toast.error("Amount required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (editingId) { await update(editingId, payload); toast.success("Receipt updated"); }
      else { await create(payload); toast.success("Receipt saved"); }
      handleOpenChange(false);
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  }

  return (
    <div className="mt-6 space-y-4">
      <div className="flex justify-between items-center">
        <div><h3 className="font-display text-xl">Receipts</h3><p className="text-sm text-stone-500">Snap and track spending across the trip.</p></div>
        <Button className="rounded-full bg-primary text-primary-foreground" onClick={openNew} data-testid="add-receipt-btn"><Plus className="w-4 h-4 mr-2" />Add receipt</Button>
        <Dialog open={open} onOpenChange={handleOpenChange}>
          <DialogContent className="rounded-2xl max-w-lg">
            <DialogHeader><DialogTitle className="font-display">{editingId ? "Edit receipt" : "New receipt"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="rounded-xl" data-testid="receipt-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Merchant</Label><Input className="rounded-xl" value={form.merchant} onChange={(e) => setForm({ ...form, merchant: e.target.value })} /></div>
              <div><Label>Amount</Label><Input type="number" step="0.01" className="rounded-xl" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="receipt-amount-input" /></div>
              <div>
                <Label>Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="rounded-xl" data-testid="receipt-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Date</Label><Input type="text" inputMode="numeric" placeholder="YYYY-MM-DD" title="Format: YYYY-MM-DD" className="rounded-xl" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              <div className="col-span-2"><Label>Notes</Label><Textarea className="rounded-xl" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <div className="col-span-2 flex items-center gap-3"><FileUploadButton label={form.photo_file_id ? "Change photo" : "Attach receipt photo"} testId="receipt-file-upload" accept="image/*,application/pdf" onUploaded={(id) => setForm((f) => ({ ...f, photo_file_id: id }))} />{form.photo_file_id && <span className="text-xs text-stone-500">Attached</span>}</div>
            </div>
            <DialogFooter><Button variant="ghost" onClick={() => handleOpenChange(false)} className="rounded-full">Cancel</Button><Button onClick={save} disabled={saving} className="rounded-full bg-primary text-primary-foreground" data-testid="save-receipt-btn">{saving ? "Saving..." : editingId ? "Save changes" : "Save"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {loading ? <div className="h-32 rounded-2xl bg-muted animate-pulse" /> :
       items.length === 0 ? (
        <Card className="rounded-2xl p-10 border-dashed border-border text-center" data-testid="empty-receipts"><Receipt className="w-8 h-8 mx-auto text-primary" /><p className="mt-2 text-stone-500">No receipts yet.</p></Card>
       ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((r) => (
            <Card key={r.id} className="rounded-2xl overflow-hidden border-border" data-testid={`receipt-${r.id}`}>
              <div className="relative h-40 bg-muted">
                <AuthImage fileId={r.photo_file_id} className="w-full h-full object-cover" fallbackClassName="w-full h-full" />
                <div className="absolute top-2 left-2 text-[10px] uppercase tracking-widest bg-white/85 backdrop-blur px-2 py-0.5 rounded-full text-stone-700">{r.category}</div>
                <div className="absolute top-1 right-1 flex items-center gap-1">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(r)} className="bg-white/70 backdrop-blur rounded-full h-7 w-7 text-stone-500 hover:text-primary" data-testid={`edit-receipt-${r.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(r.id)} className="bg-white/70 backdrop-blur rounded-full h-7 w-7 text-stone-500 hover:text-destructive" data-testid={`delete-receipt-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              <div className="p-3">
                <div className="flex items-baseline justify-between">
                  <div className="font-display text-lg">{Number(r.amount).toLocaleString()}</div>
                  <div className="text-xs text-stone-500">{r.currency}</div>
                </div>
                <div className="text-sm text-stone-600 truncate">{r.merchant || "—"}</div>
                {r.date && <div className="text-xs text-stone-400">{r.date}</div>}
              </div>
            </Card>
          ))}
        </div>
       )}
    </div>
  );
}
